/**
 * FASE 1B - Pagina Entrate
 * Gestione completa entrate automatiche e manuali
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowUpCircle, ArrowDownCircle, Euro, Search, Filter, Plus,
  Calendar, ChevronLeft, ChevronRight, Building, ExternalLink,
  Download, Upload, X, Check, Edit2, Trash2, Eye, FileText,
  RefreshCw, TrendingUp, Wallet, CreditCard, Clock, User
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' }
];

const INCOME_CATEGORIES = [
  { value: 'prenotazione', label: 'Prenotazione', color: 'blue' },
  { value: 'extra', label: 'Extra', color: 'green' },
  { value: 'cauzione', label: 'Cauzione', color: 'amber' },
  { value: 'late_checkout', label: 'Late Check-out', color: 'orange' },
  { value: 'early_checkin', label: 'Early Check-in', color: 'yellow' },
  { value: 'servizi', label: 'Servizi', color: 'purple' },
  { value: 'rimborso', label: 'Rimborso', color: 'red' },
  { value: 'altri_ricavi', label: 'Altri Ricavi', color: 'cyan' }
];

const CHANNELS = [
  { value: 'booking_com', label: 'Booking.com', color: 'blue' },
  { value: 'airbnb', label: 'Airbnb', color: 'red' },
  { value: 'expedia', label: 'Expedia', color: 'yellow' },
  { value: 'diretta', label: 'Diretta', color: 'green' },
  { value: 'website', label: 'Website', color: 'teal' },
  { value: 'telefono', label: 'Telefono', color: 'gray' },
  { value: 'whatsapp', label: 'WhatsApp', color: 'emerald' },
  { value: 'altro', label: 'Altro', color: 'slate' }
];

const PAYMENT_METHODS = [
  { value: 'contanti', label: 'Contanti' },
  { value: 'carta', label: 'Carta' },
  { value: 'bonifico', label: 'Bonifico' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'satispay', label: 'Satispay' },
  { value: 'ota_collect', label: 'Pagamento OTA' },
  { value: 'altro', label: 'Altro' }
];

const STATI = [
  { value: 'da_incassare', label: 'Da Incassare', color: 'amber' },
  { value: 'parziale', label: 'Parziale', color: 'orange' },
  { value: 'incassata', label: 'Incassata', color: 'green' }
];

export default function EconomicIncomes() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [incomes, setIncomes] = useState([]);
  const [totals, setTotals] = useState({});
  const [settings, setSettings] = useState({ iva_percent: 10 });
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [units, setUnits] = useState([]);
  
  // Filters
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [filterTipo, setFilterTipo] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterCanale, setFilterCanale] = useState('');
  const [filterStato, setFilterStato] = useState('');
  const [filterStruttura, setFilterStruttura] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Modal Dettaglio Prenotazione
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [bookingDetail, setBookingDetail] = useState(null);
  
  // Pagamenti
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    importo: '',
    data: new Date().toISOString().split('T')[0],
    metodo: 'contanti',
    note: ''
  });
  
  // View mode
  const [viewMode, setViewMode] = useState('table'); // table, cards

  const years = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2
  ];

  const fetchIncomes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        anno: selectedYear,
        mese: selectedMonth,
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (filterTipo) params.append('tipo', filterTipo);
      if (filterCategoria) params.append('categoria', filterCategoria);
      if (filterCanale) params.append('canale', filterCanale);
      if (filterStato) params.append('stato', filterStato);
      if (filterStruttura) params.append('struttura', filterStruttura);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(
        `${API}/admin/economic/incomes?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIncomes(response.data.incomes || []);
      setTotals(response.data.totals || {});
      setSettings(response.data.settings || { iva_percent: 10 });
      setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
    } catch (error) {
      console.error('Error fetching incomes:', error);
      toast.error('Errore nel caricamento delle entrate');
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth, pagination.page, pagination.limit, filterTipo, filterCategoria, filterCanale, filterStato, filterStruttura, searchQuery]);

  const fetchUnits = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/admin/economic/units`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUnits(response.data.units || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchIncomes();
      fetchUnits();
    }
  }, [token, fetchIncomes, fetchUnits]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  };

  const calculateNights = (checkin, checkout) => {
    if (!checkin || !checkout) return 0;
    const start = new Date(checkin);
    const end = new Date(checkout);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Fetch booking detail for modal
  const fetchBookingDetail = async (bookingId) => {
    if (!bookingId) return;
    setLoadingDetail(true);
    try {
      const response = await axios.get(
        `${API}/admin/bookings/${bookingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookingDetail(response.data);
    } catch (error) {
      console.error('Error fetching booking detail:', error);
      toast.error('Errore nel caricamento dettagli');
    } finally {
      setLoadingDetail(false);
    }
  };

  const openDetailModal = (income) => {
    setSelectedIncome(income);
    setShowDetailModal(true);
    setPayments([]);
    setShowPaymentForm(false);
    setEditingPayment(null);
    if (income.booking_id) {
      fetchBookingDetail(income.booking_id);
      fetchPayments(income.booking_id);
    } else {
      setBookingDetail(null);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedIncome(null);
    setBookingDetail(null);
    setPayments([]);
    setShowPaymentForm(false);
    setEditingPayment(null);
  };

  // Fetch pagamenti per una prenotazione
  const fetchPayments = async (bookingId) => {
    if (!bookingId) return;
    setLoadingPayments(true);
    try {
      const response = await axios.get(`${API}/admin/bookings/${bookingId}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Aggiungi/Modifica pagamento
  const handleSavePayment = async () => {
    if (!paymentForm.importo || !selectedIncome?.booking_id) {
      toast.error('Inserisci un importo valido');
      return;
    }
    
    try {
      if (editingPayment) {
        await axios.put(
          `${API}/admin/bookings/${selectedIncome.booking_id}/payments/${editingPayment.id}`,
          paymentForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Pagamento aggiornato');
      } else {
        await axios.post(
          `${API}/admin/bookings/${selectedIncome.booking_id}/payments`,
          paymentForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Pagamento registrato');
      }
      
      // Ricarica pagamenti e dati
      fetchPayments(selectedIncome.booking_id);
      fetchIncomes();
      setShowPaymentForm(false);
      setEditingPayment(null);
      setPaymentForm({ importo: '', data: new Date().toISOString().split('T')[0], metodo: 'contanti', note: '' });
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  // Elimina pagamento
  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Eliminare questo pagamento?')) return;
    
    try {
      await axios.delete(
        `${API}/admin/bookings/${selectedIncome.booking_id}/payments/${paymentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Pagamento eliminato');
      fetchPayments(selectedIncome.booking_id);
      fetchIncomes();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Apri form modifica pagamento
  const openEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({
      importo: payment.importo,
      data: payment.data,
      metodo: payment.metodo || 'contanti',
      note: payment.note || ''
    });
    setShowPaymentForm(true);
  };

  const prevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const nextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const openCreateModal = () => {
    setEditingIncome(null);
    setFormData({
      categoria: 'extra',
      importo_lordo: '',
      commissione_ota: 0,
      data_competenza: new Date().toISOString().split('T')[0],
      stato: 'da_incassare',
      descrizione: ''
    });
    setShowModal(true);
  };

  const openEditModal = (income) => {
    if (income.tipo === 'automatica') {
      toast.info('Le entrate automatiche non possono essere modificate direttamente');
      return;
    }
    setEditingIncome(income);
    setFormData({
      categoria: income.categoria,
      canale: income.canale,
      importo_lordo: income.importo_lordo,
      commissione_ota: income.commissione_ota || 0,
      data_competenza: income.data_competenza,
      data_incasso: income.data_incasso,
      metodo_pagamento: income.metodo_pagamento,
      stato: income.stato,
      descrizione: income.descrizione,
      note: income.note,
      booking_id: income.booking_id,
      nome_ospite: income.nome_ospite,
      unit_id: income.unit_id
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingIncome) {
        await axios.put(
          `${API}/admin/economic/incomes/${editingIncome.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Entrata aggiornata');
      } else {
        await axios.post(
          `${API}/admin/economic/incomes`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Entrata creata');
      }
      setShowModal(false);
      fetchIncomes();
    } catch (error) {
      console.error('Error saving income:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (income) => {
    if (income.tipo === 'automatica') {
      toast.error('Le entrate automatiche non possono essere eliminate');
      return;
    }
    
    if (!window.confirm('Sei sicuro di voler eliminare questa entrata?')) return;
    
    try {
      await axios.delete(
        `${API}/admin/economic/incomes/${income.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Entrata eliminata');
      fetchIncomes();
    } catch (error) {
      console.error('Error deleting income:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getCategoryColor = (cat) => {
    const category = INCOME_CATEGORIES.find(c => c.value === cat);
    return category?.color || 'gray';
  };

  const getChannelColor = (ch) => {
    const channel = CHANNELS.find(c => c.value === ch);
    return channel?.color || 'gray';
  };

  // Helper per badge canale con colori evidenti
  const renderChannelBadge = (canale, canale_label) => {
    const channelValue = canale || '';
    const label = canale_label || canale || '-';
    
    // Definisci stili specifici per ogni canale
    const channelStyles = {
      booking_com: 'bg-blue-600 text-white border-blue-700',
      airbnb: 'bg-[#FF5A5F] text-white border-[#FF5A5F]',
      expedia: 'bg-yellow-500 text-white border-yellow-600',
      diretta: 'bg-emerald-500 text-white border-emerald-600',
      website: 'bg-teal-500 text-white border-teal-600',
      telefono: 'bg-gray-500 text-white border-gray-600',
      whatsapp: 'bg-green-500 text-white border-green-600',
      altro: 'bg-slate-400 text-white border-slate-500'
    };
    
    const style = channelStyles[channelValue] || 'bg-gray-100 text-gray-600 border-gray-200';
    
    // Icone per i canali principali
    const getChannelIcon = () => {
      switch(channelValue) {
        case 'booking_com':
          return <span className="font-bold">B</span>;
        case 'airbnb':
          return <span className="font-bold">A</span>;
        case 'diretta':
          return <Check className="w-3 h-3" />;
        case 'whatsapp':
          return <span className="text-[10px]">📱</span>;
        case 'telefono':
          return <span className="text-[10px]">📞</span>;
        default:
          return null;
      }
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${style}`}>
        {getChannelIcon()}
        <span>{label}</span>
      </span>
    );
  };

  const getStatoColor = (stato) => {
    const s = STATI.find(st => st.value === stato);
    return s?.color || 'gray';
  };

  // Detect mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <AdminLayout title="Gestione Entrate">
      <div className="space-y-4 pb-20 md:pb-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="font-cinzel text-xl md:text-2xl text-[#1A202C]">Entrate</h1>
            <p className="text-sm text-gray-500">Gestione entrate automatiche e manuali</p>
          </div>
          
          {/* Month Navigator */}
          <div className="flex items-center gap-2 bg-white rounded-lg border px-2 py-1 w-full md:w-auto justify-center">
            <Button variant="ghost" size="sm" onClick={prevMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 min-w-[140px] justify-center">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="border-0 bg-transparent font-medium text-center appearance-none cursor-pointer text-sm"
              >
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border-0 bg-transparent text-gray-500 appearance-none cursor-pointer text-sm"
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <Button variant="ghost" size="sm" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* KPI Summary Cards - Con conteggio Prenotazioni e Notti */}
        <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-2 md:gap-3">
          {/* Prenotazioni e Notti - ben visibili */}
          <Card className="border-l-4 border-l-sky-500 bg-sky-50">
            <CardContent className="p-2 md:p-3">
              <p className="text-[10px] md:text-xs text-sky-600 uppercase font-semibold">Prenotazioni</p>
              <p className="text-xl md:text-2xl font-bold text-sky-700">{totals.num_prenotazioni || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-violet-500 bg-violet-50">
            <CardContent className="p-2 md:p-3">
              <p className="text-[10px] md:text-xs text-violet-600 uppercase font-semibold">Notti</p>
              <p className="text-xl md:text-2xl font-bold text-violet-700">{totals.totale_notti || 0}</p>
            </CardContent>
          </Card>
          
          {/* Importi economici */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-2 md:p-3">
              <p className="text-[10px] md:text-xs text-gray-500 uppercase">Lordo</p>
              <p className="text-base md:text-lg font-bold text-blue-600">{formatCurrency(totals.lordo)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-2 md:p-3">
              <p className="text-[10px] md:text-xs text-gray-500 uppercase">Comm. OTA</p>
              <p className="text-base md:text-lg font-bold text-orange-600">-{formatCurrency(totals.commissioni_ota)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-2 md:p-3">
              <p className="text-[10px] md:text-xs text-gray-500 uppercase">Transazioni</p>
              <p className="text-base md:text-lg font-bold text-purple-600">-{formatCurrency(totals.commissioni_transazione)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-2 md:p-3">
              <p className="text-[10px] md:text-xs text-gray-500 uppercase">Cedolare</p>
              <p className="text-base md:text-lg font-bold text-red-600">-{formatCurrency(totals.cedolare_secca)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-gray-500">
            <CardContent className="p-2 md:p-3">
              <p className="text-[10px] md:text-xs text-gray-500 uppercase">Tot. Trattenute</p>
              <p className="text-base md:text-lg font-bold text-gray-600">-{formatCurrency(totals.totale_trattenute)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-2 md:p-3">
              <p className="text-[10px] md:text-xs text-gray-500 uppercase">Netto</p>
              <p className="text-base md:text-lg font-bold text-emerald-600">{formatCurrency(totals.netto)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-2 md:p-3">
              <p className="text-[10px] md:text-xs text-gray-500 uppercase">Da Incassare</p>
              <p className="text-base md:text-lg font-bold text-amber-600">{formatCurrency(totals.da_incassare)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-2 md:p-3">
              <p className="text-[10px] md:text-xs text-gray-500 uppercase">Incassato</p>
              <p className="text-base md:text-lg font-bold text-green-600">{formatCurrency(totals.incassato)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Badge Incassato per Metodo di Pagamento */}
        {totals.incassato_per_metodo && Object.keys(totals.incassato_per_metodo).length > 0 && (
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-4 h-4 text-green-600" />
                <span className="text-xs font-semibold text-green-700 uppercase">Incassato per Metodo</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(totals.incassato_per_metodo).map(([metodo, importo]) => {
                  const metodoInfo = PAYMENT_METHODS.find(m => m.value === metodo);
                  const label = metodoInfo?.label || metodo.charAt(0).toUpperCase() + metodo.slice(1);
                  // Colori per ogni metodo
                  const colorMap = {
                    contanti: 'bg-green-100 text-green-800 border-green-300',
                    carta: 'bg-blue-100 text-blue-800 border-blue-300',
                    bonifico: 'bg-purple-100 text-purple-800 border-purple-300',
                    paypal: 'bg-indigo-100 text-indigo-800 border-indigo-300',
                    satispay: 'bg-red-100 text-red-800 border-red-300',
                    ota_collect: 'bg-orange-100 text-orange-800 border-orange-300',
                    altro: 'bg-gray-100 text-gray-800 border-gray-300'
                  };
                  const colorClass = colorMap[metodo] || colorMap.altro;
                  return (
                    <span 
                      key={metodo}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${colorClass}`}
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>{label}:</span>
                      <span className="font-bold">{formatCurrency(importo)}</span>
                    </span>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters Bar */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Cerca ospite, codice..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Tutti i tipi</option>
              <option value="automatica">Automatiche</option>
              <option value="manuale">Manuali</option>
            </select>
            <select
              value={filterStato}
              onChange={(e) => setFilterStato(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Tutti gli stati</option>
              {STATI.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="hidden md:flex"
            >
              <Filter className="w-4 h-4 mr-1" />
              Filtri
            </Button>
            <Button variant="outline" size="sm" onClick={fetchIncomes}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Extended Filters */}
        {showFilters && (
          <Card className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Categoria</label>
                <select
                  value={filterCategoria}
                  onChange={(e) => setFilterCategoria(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Tutte</option>
                  {INCOME_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Canale</label>
                <select
                  value={filterCanale}
                  onChange={(e) => setFilterCanale(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Tutti</option>
                  {CHANNELS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Struttura</label>
                <select
                  value={filterStruttura}
                  onChange={(e) => setFilterStruttura(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Tutte</option>
                  {units.map(u => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterCategoria('');
                    setFilterCanale('');
                    setFilterStruttura('');
                    setFilterTipo('');
                    setFilterStato('');
                    setSearchQuery('');
                  }}
                >
                  Reset filtri
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Data Table / Cards */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C5A059]"></div>
          </div>
        ) : incomes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Euro className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Nessuna entrata trovata</p>
              <Button onClick={openCreateModal} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi Entrata
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop Table - Con trattenute dettagliate come sistema legacy */}
            <div className="hidden lg:block">
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      {/* Row with column totals at the TOP */}
                      <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                        <tr>
                          <th colSpan="2" className="text-left p-2 font-bold">TOTALI</th>
                          <th className="text-left p-2"></th>
                          <th className="text-left p-2"></th>
                          <th className="text-left p-2"></th>
                          <th className="text-right p-2 font-bold">{formatCurrency(totals.lordo || 0)}</th>
                          <th className="text-right p-2 font-bold text-cyan-200">-{formatCurrency(totals.tassa_soggiorno || 0)}</th>
                          <th className="text-right p-2 font-bold text-teal-200">{formatCurrency(totals.imponibile || 0)}</th>
                          <th className="text-right p-2 font-bold text-yellow-200">-{formatCurrency(totals.iva || 0)}</th>
                          <th className="text-right p-2 font-bold text-orange-200">-{formatCurrency(totals.commissioni_ota || 0)}</th>
                          <th className="text-right p-2 font-bold text-purple-200">-{formatCurrency(totals.commissioni_transazione || 0)}</th>
                          <th className="text-right p-2 font-bold text-red-200">-{formatCurrency(totals.cedolare_secca || 0)}</th>
                          <th className="text-right p-2 font-bold text-green-200 bg-blue-800">{formatCurrency(totals.netto || 0)}</th>
                          <th className="text-center p-2"></th>
                          <th className="text-center p-2"></th>
                        </tr>
                      </thead>
                      {/* Column headers */}
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-2 font-medium text-gray-600">Arrivo</th>
                          <th className="text-center p-2 font-medium text-gray-600" title="Adulti + Bambini">Osp.</th>
                          <th className="text-left p-2 font-medium text-gray-600 min-w-[110px]">Ospite</th>
                          <th className="text-left p-2 font-medium text-gray-600">Canale</th>
                          <th className="text-left p-2 font-medium text-gray-600">Struttura</th>
                          <th className="text-right p-2 font-medium text-gray-600">Lordo</th>
                          <th className="text-right p-2 font-medium text-gray-600 text-cyan-600" title="Solo adulti pagano">T.Sogg.</th>
                          <th className="text-right p-2 font-medium text-gray-600 text-teal-600" title="Lordo - Tassa Soggiorno">Imponibile</th>
                          <th className="text-right p-2 font-medium text-gray-600 text-yellow-600" title={`Solo OTA, ${settings.iva_percent || 10}% su imponibile`}>IVA {settings.iva_percent || 10}%</th>
                          <th className="text-right p-2 font-medium text-gray-600 text-orange-600">OTA</th>
                          <th className="text-right p-2 font-medium text-gray-600 text-purple-600">Trans.</th>
                          <th className="text-right p-2 font-medium text-gray-600 text-red-600">Cedol.</th>
                          <th className="text-right p-2 font-medium text-gray-600 bg-emerald-50 text-emerald-700" title="Imponibile - IVA - OTA - Trans - Cedol">DA INCASSARE</th>
                          <th className="text-right p-2 font-medium text-gray-600 bg-green-50 text-green-700" title="Acconto già ricevuto">INCASSATO</th>
                          <th className="text-center p-2 font-medium text-gray-600">Stato</th>
                          <th className="text-center p-2 font-medium text-gray-600">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {incomes.map((income) => {
                          const notti = calculateNights(income.data_checkin, income.data_checkout);
                          // Da Incassare viene dal backend (importo_netto)
                          const daIncassare = income.importo_netto || 0;
                          return (
                          <tr key={income.id} className="border-b hover:bg-gray-50">
                            <td className="p-2 text-gray-600 whitespace-nowrap">
                              <div>{formatDateShort(income.data_checkin)}</div>
                              {income.data_checkout && (
                                <div className="text-[10px] text-gray-400">→ {formatDateShort(income.data_checkout)}</div>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              <div className="flex flex-col items-center">
                                <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium text-[10px]">
                                  {income.num_adulti || 1}A
                                  {(income.num_bambini > 0) && <span className="ml-0.5">+{income.num_bambini}B</span>}
                                </span>
                                {notti > 0 && (
                                  <span className="text-[9px] text-gray-400">{notti}n</span>
                                )}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="font-medium text-gray-800 truncate max-w-[110px]" title={income.nome_ospite || income.descrizione}>
                                {income.nome_ospite || income.descrizione || '-'}
                              </div>
                              <div className="flex items-center gap-1">
                                {income.codice_prenotazione && (
                                  <span className="text-[10px] text-gray-400">{income.codice_prenotazione}</span>
                                )}
                                <span className={`px-1 py-0.5 rounded text-[9px] ${
                                  income.tipo === 'automatica' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
                                }`}>
                                  {income.tipo === 'automatica' ? 'AUTO' : 'MAN'}
                                </span>
                              </div>
                            </td>
                            <td className="p-2">
                              {renderChannelBadge(income.canale, income.canale_label)}
                            </td>
                            <td className="p-2 text-gray-500 text-[11px] truncate max-w-[80px]" title={income.nome_struttura}>
                              {income.nome_struttura || '-'}
                            </td>
                            <td className="p-2 text-right font-medium">{formatCurrency(income.importo_lordo)}</td>
                            <td className="p-2 text-right text-cyan-600" title={income.tassa_soggiorno > 0 ? `${income.num_adulti || 1} adulti x ${income.num_notti || 1} notti` : 'Non applicabile'}>
                              {income.tassa_soggiorno > 0 ? `-${formatCurrency(income.tassa_soggiorno)}` : '-'}
                            </td>
                            <td className="p-2 text-right text-teal-600 font-medium" title="Lordo - Tassa Soggiorno">
                              {formatCurrency(income.imponibile || (income.importo_lordo - (income.tassa_soggiorno || 0)))}
                            </td>
                            <td className="p-2 text-right text-yellow-600" title={income.iva > 0 ? `${income.iva_pct || 10}% su imponibile (solo OTA)` : 'Dirette: NO IVA'}>
                              {income.iva > 0 ? `-${formatCurrency(income.iva)}` : '-'}
                            </td>
                            <td className="p-2 text-right text-orange-600">
                              {income.commissione_ota > 0 ? `-${formatCurrency(income.commissione_ota)}` : '-'}
                              {income.commissione_ota_pct > 0 && (
                                <div className="text-[9px] text-orange-400">({income.commissione_ota_pct}%)</div>
                              )}
                            </td>
                            <td className="p-2 text-right text-purple-600">
                              {income.commissione_transazione > 0 ? `-${formatCurrency(income.commissione_transazione)}` : '-'}
                            </td>
                            <td className="p-2 text-right text-red-600">
                              {income.cedolare_secca > 0 ? `-${formatCurrency(income.cedolare_secca)}` : '-'}
                            </td>
                            <td className="p-2 text-right font-bold text-emerald-600 bg-emerald-50">{formatCurrency(daIncassare)}</td>
                            <td className="p-2 text-right bg-green-50">
                              {income.acconto_importo > 0 ? (
                                <div className="flex flex-col items-end gap-0.5">
                                  <span className="font-bold text-green-600">{formatCurrency(income.acconto_importo)}</span>
                                  {income.metodi_pagamento_lista && income.metodi_pagamento_lista.length > 0 && (
                                    <div className="flex flex-wrap gap-0.5 justify-end">
                                      {income.metodi_pagamento_lista.map((metodo, idx) => {
                                        const metodoInfo = PAYMENT_METHODS.find(m => m.value === metodo);
                                        const label = metodoInfo?.label || metodo;
                                        const shortLabel = label.substring(0, 3).toUpperCase();
                                        const colorMap = {
                                          contanti: 'bg-green-200 text-green-800',
                                          carta: 'bg-blue-200 text-blue-800',
                                          bonifico: 'bg-purple-200 text-purple-800',
                                          paypal: 'bg-indigo-200 text-indigo-800',
                                          satispay: 'bg-red-200 text-red-800',
                                          ota_collect: 'bg-orange-200 text-orange-800',
                                          altro: 'bg-gray-200 text-gray-800'
                                        };
                                        return (
                                          <span 
                                            key={idx}
                                            className={`px-1 py-0.5 rounded text-[8px] font-semibold ${colorMap[metodo] || colorMap.altro}`}
                                            title={label}
                                          >
                                            {shortLabel}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ) : '-'}
                            </td>
                            <td className="p-2 text-center">
                              <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                                income.stato === 'incassata' ? 'bg-green-100 text-green-700' : 
                                income.stato === 'parziale' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {income.stato === 'incassata' ? 'Saldato' : income.stato === 'parziale' ? 'Parziale' : 'Attesa'}
                              </span>
                            </td>
                            <td className="p-2 text-center">
                              <div className="flex gap-0.5 justify-center">
                                <Button variant="ghost" size="sm" onClick={() => openDetailModal(income)} title="Vedi dettaglio">
                                  <Eye className="w-4 h-4 text-blue-500" />
                                </Button>
                                {income.tipo === 'manuale' && (
                                  <>
                                    <Button variant="ghost" size="sm" onClick={() => openEditModal(income)}>
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(income)} className="text-red-500">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tablet Table (medium screens) */}
            <div className="hidden md:block lg:hidden">
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                        <tr>
                          <th colSpan="2" className="text-left p-2 font-bold">TOTALI</th>
                          <th className="text-right p-2 font-bold">{formatCurrency(totals.lordo || 0)}</th>
                          <th className="text-right p-2 font-bold">-{formatCurrency((totals.commissioni_ota || 0) + (totals.commissioni_transazione || 0) + (totals.cedolare_secca || 0))}</th>
                          <th className="text-right p-2 font-bold text-yellow-200 bg-blue-800">{formatCurrency((totals.lordo || 0) - (totals.commissioni_ota || 0) - (totals.commissioni_transazione || 0) - (totals.cedolare_secca || 0))}</th>
                          <th className="text-center p-2"></th>
                        </tr>
                      </thead>
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-2 font-medium text-gray-600">Data</th>
                          <th className="text-left p-2 font-medium text-gray-600">Ospite</th>
                          <th className="text-right p-2 font-medium text-gray-600">Lordo</th>
                          <th className="text-right p-2 font-medium text-gray-600">Trattenute</th>
                          <th className="text-right p-2 font-medium text-gray-600 bg-emerald-50">Da Incassare</th>
                          <th className="text-center p-2 font-medium text-gray-600">Stato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {incomes.map((income) => {
                          const daIncassare = (income.importo_lordo || 0) - (income.commissione_ota || 0) - (income.commissione_transazione || 0) - (income.cedolare_secca || 0);
                          return (
                          <tr key={income.id} className="border-b hover:bg-gray-50">
                            <td className="p-2 text-gray-600">{formatDate(income.data_competenza || income.data_checkin)}</td>
                            <td className="p-2">
                              <div className="font-medium text-gray-800 truncate max-w-[120px]">{income.nome_ospite || income.descrizione || '-'}</div>
                              <div className="mt-1">{renderChannelBadge(income.canale, income.canale_label)}</div>
                            </td>
                            <td className="p-2 text-right font-medium">{formatCurrency(income.importo_lordo)}</td>
                            <td className="p-2 text-right text-gray-500">-{formatCurrency((income.commissione_ota || 0) + (income.commissione_transazione || 0) + (income.cedolare_secca || 0))}</td>
                            <td className="p-2 text-right font-bold text-emerald-600 bg-emerald-50">{formatCurrency(daIncassare)}</td>
                            <td className="p-2 text-center">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                                  income.stato === 'incassata' ? 'bg-green-100 text-green-700' : 
                                  income.stato === 'parziale' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {income.stato === 'incassata' ? 'Saldato' : income.stato === 'parziale' ? 'Parziale' : 'Attesa'}
                                </span>
                                {income.metodi_pagamento_lista && income.metodi_pagamento_lista.length > 0 && (
                                  <span className="text-[9px] text-gray-500">
                                    {income.metodi_pagamento_lista.map(m => {
                                      const info = PAYMENT_METHODS.find(p => p.value === m);
                                      return info?.label || m;
                                    }).join(', ')}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Mobile Cards - Con dettaglio trattenute */}
            <div className="md:hidden space-y-3">
              {incomes.map((income) => {
                const daIncassare = (income.importo_lordo || 0) - (income.commissione_ota || 0) - (income.commissione_transazione || 0) - (income.cedolare_secca || 0);
                return (
                <Card key={income.id} className="overflow-hidden">
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-gray-800 text-sm">{income.nome_ospite || income.descrizione || 'Entrata'}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(income.data_competenza || income.data_checkin)}
                        </div>
                        {/* Badge Canale ben visibile */}
                        <div className="mt-1.5">
                          {renderChannelBadge(income.canale, income.canale_label)}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                        income.tipo === 'automatica' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        {income.tipo === 'automatica' ? 'Auto' : 'Man.'}
                      </span>
                    </div>
                    
                    {/* Riepilogo Importi */}
                    <div className="bg-gray-50 rounded-lg p-2 my-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-gray-500">Lordo</span>
                        <span className="font-medium">{formatCurrency(income.importo_lordo)}</span>
                      </div>
                      
                      {/* Trattenute dettagliate */}
                      <div className="border-t border-gray-200 pt-1 mt-1 space-y-0.5">
                        {income.commissione_ota > 0 && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-orange-600">OTA ({income.commissione_ota_pct || 0}%)</span>
                            <span className="text-orange-600">-{formatCurrency(income.commissione_ota)}</span>
                          </div>
                        )}
                        {income.commissione_transazione > 0 && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-purple-600">Trans. ({income.commissione_transazione_pct || 0}%)</span>
                            <span className="text-purple-600">-{formatCurrency(income.commissione_transazione)}</span>
                          </div>
                        )}
                        {income.cedolare_secca > 0 && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-red-600">Cedolare ({income.cedolare_secca_pct || 0}%)</span>
                            <span className="text-red-600">-{formatCurrency(income.cedolare_secca)}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Da Incassare finale */}
                      <div className="border-t border-gray-300 pt-1 mt-1 flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-700">DA INCASSARE</span>
                        <span className="font-bold text-emerald-600">{formatCurrency(daIncassare)}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-0.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs inline-block ${
                          income.stato === 'incassata' ? 'bg-green-100 text-green-700' : 
                          income.stato === 'parziale' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {STATI.find(s => s.value === income.stato)?.label || income.stato}
                        </span>
                        {income.metodi_pagamento_lista && income.metodi_pagamento_lista.length > 0 && (
                          <span className="text-[10px] text-gray-500">
                            {income.metodi_pagamento_lista.map(m => {
                              const info = PAYMENT_METHODS.find(p => p.value === m);
                              return info?.label || m;
                            }).join(', ')}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {income.booking_id && (
                          <Link to={`/admin/bookings/${income.booking_id}`}>
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                        )}
                        {income.tipo === 'manuale' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openEditModal(income)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(income)} className="text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
              })}
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-4 py-2 text-sm">
              Pagina {pagination.page} di {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Mobile FAB */}
        <div className="md:hidden fixed bottom-20 right-4 z-50">
          <Button
            onClick={openCreateModal}
            className="rounded-full w-14 h-14 shadow-lg bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </div>

        {/* Desktop Add Button */}
        <div className="hidden md:flex justify-end">
          <Button onClick={openCreateModal} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Entrata
          </Button>
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{editingIncome ? 'Modifica Entrata' : 'Nuova Entrata'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Categoria *</label>
                    <select
                      value={formData.categoria || ''}
                      onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      {INCOME_CATEGORIES.filter(c => c.value !== 'prenotazione').map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Canale</label>
                    <select
                      value={formData.canale || ''}
                      onChange={(e) => setFormData({ ...formData, canale: e.target.value })}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      <option value="">Nessuno</option>
                      {CHANNELS.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-1">Descrizione</label>
                  <Input
                    value={formData.descrizione || ''}
                    onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                    placeholder="Descrizione entrata..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Importo Lordo *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.importo_lordo || ''}
                      onChange={(e) => setFormData({ ...formData, importo_lordo: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Commissione</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.commissione_ota || ''}
                      onChange={(e) => setFormData({ ...formData, commissione_ota: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Data Competenza *</label>
                    <Input
                      type="date"
                      value={formData.data_competenza || ''}
                      onChange={(e) => setFormData({ ...formData, data_competenza: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Data Incasso</label>
                    <Input
                      type="date"
                      value={formData.data_incasso || ''}
                      onChange={(e) => setFormData({ ...formData, data_incasso: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Metodo Pagamento</label>
                    <select
                      value={formData.metodo_pagamento || ''}
                      onChange={(e) => setFormData({ ...formData, metodo_pagamento: e.target.value })}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      <option value="">Seleziona...</option>
                      {PAYMENT_METHODS.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Stato *</label>
                    <select
                      value={formData.stato || 'da_incassare'}
                      onChange={(e) => setFormData({ ...formData, stato: e.target.value })}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      {STATI.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-1">Struttura</label>
                  <select
                    value={formData.unit_id || ''}
                    onChange={(e) => setFormData({ ...formData, unit_id: e.target.value })}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="">Nessuna</option>
                    {units.map(u => (
                      <option key={u.id} value={u.id}>{u.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-1">Nome Ospite</label>
                  <Input
                    value={formData.nome_ospite || ''}
                    onChange={(e) => setFormData({ ...formData, nome_ospite: e.target.value })}
                    placeholder="Nome ospite (opzionale)"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-1">Note</label>
                  <textarea
                    value={formData.note || ''}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 h-20"
                    placeholder="Note aggiuntive..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowModal(false)}>
                    Annulla
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Salva
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Modal Dettaglio Prenotazione/Entrata */}
        {showDetailModal && selectedIncome && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="border-b bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-t-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg text-white">Dettaglio Entrata</CardTitle>
                    <p className="text-sm text-slate-300 mt-1">
                      {selectedIncome.codice_prenotazione || selectedIncome.id?.substring(0, 8)}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={closeDetailModal} className="text-white hover:bg-slate-700">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {loadingDetail ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
                  </div>
                ) : (
                  <>
                    {/* Info Ospite e Prenotazione */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                          <User className="w-3 h-3" />
                          OSPITE
                        </div>
                        <p className="font-medium text-gray-800">
                          {selectedIncome.nome_ospite || bookingDetail?.nome_ospite || '-'}
                        </p>
                        {(bookingDetail?.email || bookingDetail?.telefono) && (
                          <div className="text-xs text-gray-500 mt-1">
                            {bookingDetail?.email && <div>{bookingDetail.email}</div>}
                            {bookingDetail?.telefono && <div>{bookingDetail.telefono}</div>}
                          </div>
                        )}
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                          <Building className="w-3 h-3" />
                          STRUTTURA
                        </div>
                        <p className="font-medium text-gray-800">
                          {selectedIncome.nome_struttura || bookingDetail?.nome_alloggio || '-'}
                        </p>
                        <div className="text-xs text-gray-500 mt-1">
                          Canale: <span className="font-medium">{selectedIncome.canale_label || selectedIncome.canale}</span>
                        </div>
                      </div>
                    </div>

                    {/* Date e Notti */}
                    {selectedIncome.data_checkin && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-blue-600 text-xs mb-2">
                          <Calendar className="w-3 h-3" />
                          SOGGIORNO
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <div className="text-xs text-gray-500">Check-in</div>
                            <div className="font-bold text-gray-800">{formatDate(selectedIncome.data_checkin)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Notti</div>
                            <div className="font-bold text-2xl text-blue-600">
                              {calculateNights(selectedIncome.data_checkin, selectedIncome.data_checkout)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Check-out</div>
                            <div className="font-bold text-gray-800">{formatDate(selectedIncome.data_checkout)}</div>
                          </div>
                        </div>
                        {bookingDetail?.num_adulti && (
                          <div className="text-xs text-center text-gray-500 mt-2">
                            {bookingDetail.num_adulti} adulti
                            {bookingDetail.num_bambini > 0 && `, ${bookingDetail.num_bambini} bambini`}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Riepilogo Economico */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-100 px-3 py-2 text-sm font-medium text-gray-600">
                        Riepilogo Economico
                      </div>
                      <div className="p-3 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Importo Lordo</span>
                          <span className="font-bold">{formatCurrency(selectedIncome.importo_lordo)}</span>
                        </div>
                        
                        {/* Step 1: Tassa Soggiorno */}
                        {selectedIncome.tassa_soggiorno > 0 && (
                          <div className="flex justify-between text-sm text-cyan-600">
                            <span>Tassa Soggiorno ({selectedIncome.num_adulti || 1} adulti × {selectedIncome.num_notti || 1} notti)</span>
                            <span>-{formatCurrency(selectedIncome.tassa_soggiorno)}</span>
                          </div>
                        )}
                        
                        {/* Step 2: Imponibile */}
                        <div className="flex justify-between bg-gray-50 -mx-3 px-3 py-1 border-t border-b">
                          <span className="text-gray-700 font-medium">Imponibile</span>
                          <span className="font-medium">{formatCurrency(selectedIncome.imponibile || (selectedIncome.importo_lordo - (selectedIncome.tassa_soggiorno || 0)))}</span>
                        </div>
                        
                        <div className="space-y-1">
                          {/* IVA - solo OTA */}
                          {selectedIncome.iva > 0 ? (
                            <div className="flex justify-between text-sm text-yellow-600">
                              <span>IVA ({selectedIncome.iva_pct || 10}% su imponibile)</span>
                              <span>-{formatCurrency(selectedIncome.iva)}</span>
                            </div>
                          ) : (
                            <div className="flex justify-between text-sm text-gray-400">
                              <span>IVA (non applicabile - Diretta)</span>
                              <span>-</span>
                            </div>
                          )}
                          
                          {/* Commissione OTA */}
                          <div className="flex justify-between text-sm text-orange-600">
                            <span>Commissione OTA ({selectedIncome.commissione_ota_pct || 0}%)</span>
                            <span>{selectedIncome.commissione_ota > 0 ? `-${formatCurrency(selectedIncome.commissione_ota)}` : '-'}</span>
                          </div>
                          
                          {/* Transazione */}
                          <div className="flex justify-between text-sm text-purple-600">
                            <span>Costo Transazione ({selectedIncome.commissione_transazione_pct || 0}%)</span>
                            <span>{selectedIncome.commissione_transazione > 0 ? `-${formatCurrency(selectedIncome.commissione_transazione)}` : '-'}</span>
                          </div>
                          
                          {/* Cedolare */}
                          <div className="flex justify-between text-sm text-red-600">
                            <span>Cedolare Secca ({selectedIncome.cedolare_secca_pct || 0}%)</span>
                            <span>{selectedIncome.cedolare_secca > 0 ? `-${formatCurrency(selectedIncome.cedolare_secca)}` : '-'}</span>
                          </div>
                        </div>

                        <div className="flex justify-between text-sm text-gray-500 border-t pt-2">
                          <span>Totale Trattenute</span>
                          <span>-{formatCurrency(selectedIncome.totale_trattenute || 0)}</span>
                        </div>

                        <div className={`flex justify-between -mx-3 px-3 py-2 mt-2 border-t ${
                          selectedIncome.stato === 'incassata' ? 'bg-green-50' : 'bg-emerald-50'
                        }`}>
                          <span className={`font-bold ${selectedIncome.stato === 'incassata' ? 'text-green-700' : 'text-emerald-700'}`}>
                            {selectedIncome.stato === 'incassata' ? 'INCASSATO' : 'DA INCASSARE'}
                          </span>
                          <span className={`font-bold text-xl ${selectedIncome.stato === 'incassata' ? 'text-green-700' : 'text-emerald-700'}`}>
                            {formatCurrency(selectedIncome.importo_netto)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stato e Pagamento */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">STATO INCASSO</div>
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                          selectedIncome.stato === 'incassata' ? 'bg-green-100 text-green-700' : 
                          selectedIncome.stato === 'parziale' ? 'bg-orange-100 text-orange-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {selectedIncome.stato === 'incassata' ? 'Saldato' : 
                           selectedIncome.stato === 'parziale' ? 'Parziale' : 
                           'Da Incassare'}
                        </span>
                        {selectedIncome.data_incasso && (
                          <div className="text-xs text-gray-500 mt-2">
                            Incassato il: {formatDate(selectedIncome.data_incasso)}
                          </div>
                        )}
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">METODO PAGAMENTO</div>
                        <p className="font-medium text-gray-800 capitalize">
                          {selectedIncome.metodo_pagamento || bookingDetail?.metodo_pagamento || '-'}
                        </p>
                      </div>
                    </div>

                    {/* SEZIONE PAGAMENTI */}
                    {selectedIncome.booking_id && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-green-100 px-3 py-2 flex justify-between items-center">
                          <span className="text-sm font-medium text-green-800 flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            Storico Pagamenti
                            {payments.length > 0 && (
                              <span className="bg-green-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                                {payments.length}
                              </span>
                            )}
                          </span>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => { setShowPaymentForm(true); setEditingPayment(null); setPaymentForm({ importo: '', data: new Date().toISOString().split('T')[0], metodo: 'contanti', note: '' }); }}
                            className="h-7 text-xs border-green-600 text-green-700 hover:bg-green-50"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Registra Pagamento
                          </Button>
                        </div>
                        
                        <div className="p-3">
                          {/* Form nuovo/modifica pagamento */}
                          {showPaymentForm && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                              <h4 className="text-sm font-medium text-green-800 mb-2">
                                {editingPayment ? 'Modifica Pagamento' : 'Nuovo Pagamento'}
                              </h4>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs">Importo (€) *</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentForm.importo}
                                    onChange={(e) => setPaymentForm({...paymentForm, importo: parseFloat(e.target.value) || ''})}
                                    placeholder="0.00"
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Data *</Label>
                                  <Input
                                    type="date"
                                    value={paymentForm.data}
                                    onChange={(e) => setPaymentForm({...paymentForm, data: e.target.value})}
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Metodo</Label>
                                  <select
                                    value={paymentForm.metodo}
                                    onChange={(e) => setPaymentForm({...paymentForm, metodo: e.target.value})}
                                    className="w-full border rounded-md px-2 py-1 text-sm h-8"
                                  >
                                    <option value="contanti">Contanti</option>
                                    <option value="bonifico">Bonifico</option>
                                    <option value="carta">Carta</option>
                                    <option value="satispay">Satispay</option>
                                    <option value="paypal">PayPal</option>
                                    <option value="ota">Pagato su OTA</option>
                                    <option value="altro">Altro</option>
                                  </select>
                                </div>
                                <div>
                                  <Label className="text-xs">Note</Label>
                                  <Input
                                    value={paymentForm.note}
                                    onChange={(e) => setPaymentForm({...paymentForm, note: e.target.value})}
                                    placeholder="Opzionale"
                                    className="h-8 text-sm"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end gap-2 mt-3">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => { setShowPaymentForm(false); setEditingPayment(null); }}
                                  className="h-7 text-xs"
                                >
                                  Annulla
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={handleSavePayment}
                                  className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                >
                                  {editingPayment ? 'Salva Modifiche' : 'Registra'}
                                </Button>
                              </div>
                            </div>
                          )}
                          
                          {/* Lista pagamenti */}
                          {loadingPayments ? (
                            <div className="text-center py-4">
                              <RefreshCw className="w-5 h-5 animate-spin mx-auto text-gray-400" />
                            </div>
                          ) : payments.length === 0 ? (
                            <div className="text-center py-4 text-gray-500 text-sm">
                              <Wallet className="w-8 h-8 mx-auto mb-2 opacity-30" />
                              Nessun pagamento registrato
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {payments.map((payment) => (
                                <div 
                                  key={payment.id} 
                                  className="flex items-center justify-between p-2 bg-white border rounded-lg hover:bg-gray-50 group"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="p-1.5 bg-green-100 rounded-full">
                                      <Check className="w-3 h-3 text-green-600" />
                                    </div>
                                    <div>
                                      <p className="font-semibold text-green-700">{formatCurrency(payment.importo)}</p>
                                      <p className="text-[10px] text-gray-500">
                                        {formatDate(payment.data)} • {payment.metodo || 'N/D'}
                                        {payment.note && ` • ${payment.note}`}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => openEditPayment(payment)}
                                      className="h-7 w-7 p-0"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => handleDeletePayment(payment.id)}
                                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                              
                              {/* Totale pagamenti */}
                              <div className="flex justify-between pt-2 border-t mt-2 text-sm">
                                <span className="font-medium text-gray-600">Totale Incassato</span>
                                <span className="font-bold text-green-600">
                                  {formatCurrency(payments.reduce((sum, p) => sum + (p.importo || 0), 0))}
                                </span>
                              </div>
                              
                              {/* Saldo residuo */}
                              {selectedIncome.importo_netto && (
                                <div className="flex justify-between text-sm">
                                  <span className="font-medium text-gray-600">Saldo Residuo</span>
                                  <span className={`font-bold ${
                                    (selectedIncome.importo_netto - payments.reduce((sum, p) => sum + (p.importo || 0), 0)) <= 0 
                                      ? 'text-green-600' 
                                      : 'text-amber-600'
                                  }`}>
                                    {formatCurrency(Math.max(0, selectedIncome.importo_netto - payments.reduce((sum, p) => sum + (p.importo || 0), 0)))}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Note */}
                    {(selectedIncome.note || bookingDetail?.note_interne) && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="text-xs text-yellow-700 font-medium mb-1">NOTE</div>
                        <p className="text-sm text-yellow-800">
                          {selectedIncome.note || bookingDetail?.note_interne}
                        </p>
                      </div>
                    )}

                    {/* Azioni */}
                    <div className="flex justify-between items-center pt-4 border-t">
                      {selectedIncome.booking_id && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            closeDetailModal();
                            navigate(`/admin/bookings?open=${selectedIncome.booking_id}`);
                          }}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Vai alla Prenotazione
                        </Button>
                      )}
                      <Button variant="outline" onClick={closeDetailModal}>
                        Chiudi
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
